import { get, keys, values, isEmpty, sortBy } from 'lodash'
import { createSelector } from 'reselect'
import { filter } from 'lodash'

import { getModifierGroups } from 'store/ModifierGroups/selectors'
import {
  getPaginatedReportingCategories,
  getReportingCategories,
} from 'store/ReportingCategories/selectors'
import { getDisplayCategories } from 'store/DisplayCategories/selectors'

export const getItemsWithReportingCategory = createSelector(
  [(state) => state.items.byId, getReportingCategories],
  (items, reportingCategories) => {
    const allItems = values(items)
    const reportingCategoriesUuids = reportingCategories.map(
      (reportingCategory) => reportingCategory.uuid
    )

    const itemsWithReportingCategories = allItems?.map((item) => {
      const reportingCategoryUuid = item?.itemCategoryUuids.find(
        (itemCategoryUuid) =>
          reportingCategoriesUuids.includes(itemCategoryUuid)
      )

      return {
        ...item,
        reportingCategoryUuid,
      }
    })

    return itemsWithReportingCategories
  }
)

export const getItems = (state) => {
  const allItems = values(state.items.byId)
  const reportingCategories = getReportingCategories(state).map(
    (reportingCategory) => reportingCategory.uuid
  )

  const items = allItems?.map((item) => {
    const reportingCategoryUuid = item?.itemCategoryUuids?.find(
      (itemCategoryUuid) =>
        reportingCategories.includes(itemCategoryUuid)
    )

    return {
      ...item,
      reportingCategoryUuid,
    }
  })

  return items
}

export const getFilteredItems = (state, filterCallback) => {
  const items = getItems(state)
  return items.filter(filterCallback)
}

export const getPaginatedItems = (state) => {
  return values(state?.items?.pagination?.byId) ?? []
}

export const getTotalItemsCount = (state) =>
  state?.items?.pagination?.total ?? 0

export const getItemFromPagination = (state, itemId) => {
  const item = state?.items?.pagination?.byId?.[itemId] ?? {}

  const reportingCategories = getReportingCategories(state)?.map(
    (majorCategory) => majorCategory?.uuid
  )

  const modifierGroups = getModifierGroups(state)?.map(
    (modifierGroup) => modifierGroup?.uuid
  )

  const displayCategories = getDisplayCategories(state)?.map(
    (displayCategory) => displayCategory?.uuid
  )

  const modifierGroupsAssignedToItem =
    item?.itemCategoryUuids?.filter((itemCategoryUuid) => {
      return modifierGroups?.includes(itemCategoryUuid)
    }) ?? []

  const reportingCategoryUuid =
    item?.itemCategoryUuids?.filter((itemCategoryUuid) =>
      reportingCategories?.includes(itemCategoryUuid)
    ) ?? []

  const displayCategoriesAssignedToItem =
    item?.itemCategoryUuids?.filter((itemCategoryUuid) =>
      displayCategories?.includes(itemCategoryUuid)
    ) ?? []

  const itemWithReportingCategoryUuid = {
    ...item,
    price: item?.price
      ? new Intl.NumberFormat('en-US', {
          useGrouping: false,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(item?.price)
      : '',
    reportingCategoryUuid,
    modifierGroupsUuids: modifierGroupsAssignedToItem,
    displayCategoriesAssignedToItem,
  }

  return itemWithReportingCategoryUuid ?? {}
}

export const getItemsIndexes = (state) =>
  state?.items?.itemsIndexes ?? []

export const getItemsIndexesInModifierGroups = (state) =>
  state?.items?.itemsIndexesInModifierGroups ?? []

export const getIsItemsLoading = (state) =>
  state?.items?.isLoading ?? false

export const getFormRegisteredFields = (state) =>
  keys(get(state, 'form.item-settings.registeredFields'))

export const getSearchGlobalItemValue = (state) =>
  state?.items?.filters?.searchGlobalItemValue ?? ''

export const getItemsFilterByMenu = (state) =>
  state?.items?.filters?.byMenu ?? []

export const getItemsFilterByCategory = (state) =>
  state?.items?.filters?.byCategory ?? []

export const getItemsFilterByInventoryState = (state) =>
  state?.items?.filters?.byInventoryState ?? []

export const getFilteredItemsByMenusCategoriesAvailability =
  createSelector(
    getItems,
    getItemsFilterByMenu,
    getItemsFilterByCategory,
    getItemsFilterByInventoryState,
    (
      items,
      filtersByMenus,
      filterByCategories,
      filterByInventoryState
    ) => {
      const filterItemsByMenus = (item) => {
        if (isEmpty(filtersByMenus)) return true
        return item.menuUuids.some((menuUuid) =>
          filtersByMenus.includes(menuUuid)
        )
      }

      const filterItemsByCategories = (item) => {
        if (isEmpty(filterByCategories)) return true
        return item.itemCategoryUuids.some((menuUuid) =>
          filterByCategories.includes(menuUuid)
        )
      }

      const filterItemsByInventoryState = (item) => {
        if (isEmpty(filterByInventoryState)) return true
        return filterByInventoryState.includes(item.inventoryState)
      }

      if (
        isEmpty(filtersByMenus) &&
        isEmpty(filterByCategories) &&
        isEmpty(filterByInventoryState)
      ) {
        return items
      }

      return items.filter((item) => {
        return (
          filterItemsByMenus(item) &&
          filterItemsByCategories(item) &&
          filterItemsByInventoryState(item)
        )
      })
    }
  )

export const getAssignedItemsToDisplayCategory = createSelector(
  getItems,
  getDisplayCategories,
  (items, displayCategories) => {
    return displayCategories.map((category) => {
      return {
        categoryUuid: category.uuid,
        itemsInCategory: items.filter(
          (item) =>
            item?.itemCategoryUuids?.includes(category.uuid) ?? {}
        ),
        majorCategory: category.majorCategory ?? false,
      }
    })
  }
)

export const getCurrentAssignedDisplayCategoriesItemsUuid = (
  state,
  uuid
) => {
  const assignedItemsToDisplayCategory =
    getAssignedItemsToDisplayCategory(state)
  const assignedCurrentItemToCategory =
    assignedItemsToDisplayCategory.filter(
      (assignedItemToCategory) =>
        assignedItemToCategory.categoryUuid === uuid
    )

  const assignedCurrentItemToCutegoryUuid =
    assignedCurrentItemToCategory[0]?.itemsInCategory.map(
      (assignedItem) => assignedItem.uuid
    )
  return assignedCurrentItemToCutegoryUuid ?? []
}

export const getDisplayCategoriesListItems = createSelector(
  getDisplayCategories,
  getAssignedItemsToDisplayCategory,
  (displayCategories, assignedItemsToDisplayCategory) => {
    const categoriesListItems = sortBy(
      displayCategories.map((category) => {
        const itemsAssidgnedToCategory =
          assignedItemsToDisplayCategory.find(
            (assignedItemsToDisplayCategory) =>
              assignedItemsToDisplayCategory.categoryUuid ===
              category.uuid
          )

        return {
          ...category,
          assignedItemsLength:
            itemsAssidgnedToCategory?.itemsInCategory?.length ?? 0,
        }
      }),
      ['sortOrder', 'name']
    )

    return categoriesListItems
  }
)

export const getItemsAssignedToPaginatedReportingCategories =
  createSelector(
    [getItemsWithReportingCategory, getPaginatedReportingCategories],
    (allItems, reportingCategories) => {
      const reportingCategoriesUuids = reportingCategories.map(
        (reportingCategory) => reportingCategory.uuid
      )

      return reportingCategoriesUuids.reduce(
        (acc, reportingCategoryUuid) => {
          const items = allItems.filter((item) =>
            item.itemCategoryUuids.includes(reportingCategoryUuid)
          )

          return {
            ...acc,
            [reportingCategoryUuid]: items,
          }
        },
        {}
      )
    }
  )

export const getItemsUuidsAssignedToPaginatedReportingCategory = (
  state,
  uuid
) => {
  const itemsAssignedToReportingCategory =
    getItemsAssignedToPaginatedReportingCategories(state)

  return (
    itemsAssignedToReportingCategory[uuid]?.map(
      (item) => item.uuid
    ) ?? []
  )
}

export const getItemsWithCurrentPaginatedReportingCategory = (
  state,
  uuid
) => {
  const itemsAssignedToReportingCategory =
    getItemsAssignedToPaginatedReportingCategories(state)

  return itemsAssignedToReportingCategory[uuid] ?? []
}

export const getItemsWithoutReportingCategory = createSelector(
  getItems,
  getPaginatedReportingCategories,
  (allItems, reportingCategories) => {
    const reportingCategoriesUuids = reportingCategories.map(
      (reportingCategory) => reportingCategory.uuid
    )

    const itemsWithoutReportingCategory = allItems?.filter(
      (item) =>
        !item?.itemCategoryUuids?.some((itemCategoryUuid) =>
          reportingCategoriesUuids?.includes(itemCategoryUuid)
        )
    )

    return itemsWithoutReportingCategory
  }
)

export const getAllItemsForModifierGroups = createSelector(
  getItems,
  (items) => {
    return filter(items, (item) => item?.modifier === true)
  }
)

export const modifierGroupsWithAssignedItems = createSelector(
  getItems,
  getModifierGroups,
  (items, categories) => {
    return categories.map((category) => {
      return (
        {
          categoryUuid: category.uuid,
          itemsInCategory:
            items?.filter((item) =>
              item?.itemCategoryUuids?.includes(category.uuid)
            ) ?? {},
          majorCategory: category.majorCategory ?? false,
        } ?? []
      )
    })
  }
)

export const getAssignedItemsToCurrentModifierGroup = (
  state,
  uuid
) => {
  const allItems = modifierGroupsWithAssignedItems(state)

  const assignedCurrentItemToCategory = allItems?.filter(
    (assignedItemToCategory) =>
      assignedItemToCategory?.categoryUuid === uuid
  )

  const assignedCurrentItemToCategoryUuid =
    assignedCurrentItemToCategory[0]?.itemsInCategory.map(
      (assignedItem) => assignedItem.uuid
    )

  return assignedCurrentItemToCategoryUuid ?? []
}

export const getDefaultSelectedItemsToModifierGroup = (
  state,
  selectedItems
) => {
  const items = getItems(state)
  return filter(items, (item) => selectedItems?.includes(item.uuid))
}

// Images
export const getItemImage = (state, productSku) => {
  return state?.items?.images?.list?.[productSku] ?? null
}
